import React from 'react';
import { useTransition, animated } from 'react-spring';
import Suggestions from './suggestions/suggestions';
import PopularProducts from './popularProducts/popularProducts';
import PopularCategories from './popularCategories/popularCategories';
import Section from './section/section';
import { AttraqtSuggestionsResponse } from '../../../../../../types';
import { MessagePropertiesContext } from '../../../../../../contexts/messageProperties';
import { ANIMATION_DURATION } from '../../searchApp';
import dropdownStyles from './dropdown.module.pcss';

export interface DropdownProps {
  input: string;
  data: AttraqtSuggestionsResponse | null;
  canDisplayDropdown: boolean;
}

const howCanWeHelpIdToMatchWithTheIdInCmsComponent = 'howCanWeHelpSearch';
const howCanWeHelpCmsElement = document.getElementById(howCanWeHelpIdToMatchWithTheIdInCmsComponent)?.innerHTML || '';
const { isSearchSuggestionsEnabled } = window.bbGlobal.attraqt;

function HowCanWeHelpCms(): React.ReactElement {
  return <div dangerouslySetInnerHTML={{ __html: howCanWeHelpCmsElement }} />;
}

function Dropdown({ input, data, canDisplayDropdown }: DropdownProps): React.ReactElement {
  const [formattedMessage] = React.useContext(MessagePropertiesContext);
  const transitions = useTransition(canDisplayDropdown && data, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: ANIMATION_DURATION }
  });

  return (
    <>
      {transitions.map(({ item: dataToDisplay, key, props }) => (
        <>
          {dataToDisplay ? (
            <animated.div key={key} style={props} className={dropdownStyles.dropdown}>
              <div className={dropdownStyles['dropdown__flex-1']}>
                <div className={dropdownStyles['dropdown__flex-2']}>
                  {dataToDisplay.data.suggestion.products.length !== 0 && (
                    <Section
                      title={formattedMessage({
                        id: 'search.dropdown.popularProducts',
                        fallback: 'Popular products'
                      })}
                    >
                      <PopularProducts popularProducts={dataToDisplay.data.suggestion.products} />
                    </Section>
                  )}
                </div>
                <div className={dropdownStyles['dropdown__flex-3']}>
                  {dataToDisplay.data.suggestion.searchTerms.length !== 0 && (
                    <Section title={formattedMessage({ id: 'search.dropdown.suggestions', fallback: 'Suggestions' })}>
                      <Suggestions input={input} suggestions={dataToDisplay.data.suggestion.searchTerms} />
                    </Section>
                  )}
                  {dataToDisplay.data.suggestion.categories.length !== 0 && (
                    <Section
                      title={formattedMessage({
                        id: 'search.dropdown.popularCategories',
                        fallback: 'Popular categories'
                      })}
                    >
                      <PopularCategories popularProducts={dataToDisplay.data.suggestion.categories} />
                    </Section>
                  )}
                  <div className={dropdownStyles['cms-tablet']}>
                    <Section>
                      <HowCanWeHelpCms />
                    </Section>
                  </div>
                </div>
                <div className={dropdownStyles['cms-mobile']}>
                  <Section>
                    <HowCanWeHelpCms />
                  </Section>
                </div>
              </div>
            </animated.div>
          ) : (
            input && (
              <animated.div style={props} className={dropdownStyles.dropdown}>
                <div className={dropdownStyles['dropdown__flex-1']}>
                  {isSearchSuggestionsEnabled && (
                    <div className={dropdownStyles['dropdown__flex-2']}>
                      <Section>
                        <p>
                          {formattedMessage({
                            id: 'search.dropdown.noSuggestions',
                            fallback: `There are no suggestions for "${input}"`,
                            values: { 0: input }
                          })}
                        </p>
                      </Section>
                    </div>
                  )}
                  <div className={dropdownStyles['dropdown__flex-2']}>
                    <Section>
                      <HowCanWeHelpCms />
                    </Section>
                  </div>
                </div>
              </animated.div>
            )
          )}
        </>
      ))}
    </>
  );
}

export default Dropdown;
